import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ChevronDown, ChevronUp } from "lucide-react";
import SaleVelocityForm from "./SaleVelocityForm";

const formatINR = (value) => {
  if (!value || isNaN(value)) return "—";
  if (Math.abs(value) >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `${(value / 100000).toFixed(1)} L`;
  return `${Math.round(value).toLocaleString("en-IN")}`;
};

const ExpenseCashFlow = ({ formData, plData, fsiCosts, daChargesCosts, societyCosts }) => {
  const [viewMode, setViewMode] = useState("quarterly"); // "monthly" or "quarterly"
  const [expandedQuarters, setExpandedQuarters] = useState({});

  const cashFlowData = useMemo(() => {
    if (!plData || !formData) return null;

    const durationMonths = parseInt(formData.projectDuration) || 36;
    const preProjectMonths = 3;
    const totalMonths = preProjectMonths + durationMonths;

    // ===== GATHER ALL EXPENSE HEADS =====
    const stampDuty = daChargesCosts?.stampDuty || plData?.stampDuty || 0;
    const registration = daChargesCosts?.totalRegistration || 0;
    const daTotal = daChargesCosts?.totalDACharges || stampDuty + registration;

    const fsiTotalCosts = plData?.fsiTotalCosts || 0;
    const tdrCost = (fsiCosts?.amenityTDRCost || 0) + (fsiCosts?.slumTDRCost || 0);
    const paidFSICost = fsiCosts?.paidFSICost || 0;
    const ancillaryCost = fsiCosts?.ancillaryFSICost || 0;
    const sanctionCosts = fsiTotalCosts - tdrCost - paidFSICost - ancillaryCost;

    // Society expenses - use societyCosts directly (plData may not include them if timing issue)
    const totalRent = societyCosts?.totalRentalCost || 0;
    const monthlyRent = durationMonths > 0 ? totalRent / durationMonths : 0;
    const depositCost = societyCosts?.depositCost || 0;
    const betterment = societyCosts?.bettermentCharges || 0;
    const shifting = societyCosts?.shiftingCharges || 0;
    const corpus = societyCosts?.corpusCharges || 0;
    const brokerageSociety = societyCosts?.brokerageCost || 0;
    const parkingCompensation = societyCosts?.parkingCompensationPayout || 0;
    const pmcCost = societyCosts?.pmcCost || 0;
    const totalGST = societyCosts?.totalGST || 0;
    const bgCost = societyCosts?.bgCost || 0;

    // Other costs
    const totalMarketing = plData?.totalMarketingCost || 0;
    const totalMisc = plData?.totalMiscExp || 0;
    const totalBrokerage = plData?.totalBrokerageCommission || 0;
    const construction = plData?.totalConstructionCost || 0;
    const bankInterest = plData?.totalBankInterest || 0;

    // ===== BUILD MONTH-BY-MONTH EXPENSE ARRAY =====
    // Index: 0 = Month -3 (pre-project), 1 = Month -2, 2 = Month -1, 3 = Month 1 (project start)
    const months = [];
    for (let i = 0; i < totalMonths; i++) {
      months.push({
        index: i,
        monthLabel: i < preProjectMonths ? `M-${preProjectMonths - i}` : `M${i - preProjectMonths + 1}`,
        monthNum: i - preProjectMonths + 1, // -3, -2, -1, 1, 2, ...
        stampDuty: 0,
        registration: 0,
        tdrCost: 0,
        paidFSI: 0,
        ancillaryFSI: 0,
        sanctionCosts: 0,
        rentDeposit: 0,
        betterment: 0,
        shifting: 0,
        corpus: 0,
        monthlyRent: 0,
        bgCost: 0,
        brokerageSociety: 0,
        parkingCompensation: 0,
        marketing: 0,
        misc: 0,
        brokerage: 0,
        construction: 0,
        bankInterest: 0,
        pmcCost: 0,
        gst: 0,
        total: 0,
      });
    }

    // ===== DISTRIBUTION RULES =====

    // 1. PRE-PROJECT (Month -3 to -1): Stamp duty & Registration
    // Spread DA charges across pre-project months
    if (preProjectMonths > 0) {
      months[0].stampDuty = stampDuty;
      months[0].registration = registration;
    }

    // 2. PROJECT START (Month 1 = index 3): Sanction costs + initial society expenses
    const startIdx = preProjectMonths; // index 3
    if (startIdx < totalMonths) {
      // FSI & Sanction costs at start
      months[startIdx].tdrCost = tdrCost;
      months[startIdx].paidFSI = paidFSICost;
      months[startIdx].ancillaryFSI = ancillaryCost;
      months[startIdx].sanctionCosts = sanctionCosts;

      // Society initial: deposit, 50% betterment, 50% shifting
      months[startIdx].rentDeposit = depositCost;
      months[startIdx].betterment = betterment * 0.5;
      months[startIdx].shifting = shifting * 0.5;
      months[startIdx].bgCost = bgCost;
      months[startIdx].brokerageSociety = brokerageSociety;
      months[startIdx].parkingCompensation = parkingCompensation;
    }

    // 3. MONTHLY RENT: distributed across all project months
    for (let i = startIdx; i < totalMonths; i++) {
      months[i].monthlyRent = monthlyRent;
    }

    // 4. END OF PROJECT: corpus, remaining 50% betterment, remaining 50% shifting
    const lastIdx = totalMonths - 1;
    if (lastIdx >= startIdx) {
      months[lastIdx].corpus = corpus;
      months[lastIdx].betterment += betterment * 0.5;
      months[lastIdx].shifting += shifting * 0.5;
    }

    // 5. MARKETING: 30% Q1, 10% Q2, balance equally over remaining
    const q1End = Math.min(startIdx + 3, totalMonths);
    const q2End = Math.min(startIdx + 6, totalMonths);
    const marketingQ1 = totalMarketing * 0.30;
    const marketingQ2 = totalMarketing * 0.10;
    const remainingQuarters = Math.max(1, Math.ceil((durationMonths - 6) / 3));
    const marketingPerRemainingQ = (totalMarketing * 0.60) / remainingQuarters;

    // Q1 marketing (first 3 project months)
    for (let i = startIdx; i < q1End; i++) {
      months[i].marketing = marketingQ1 / Math.min(3, q1End - startIdx);
    }
    // Q2 marketing (months 4-6)
    for (let i = q1End; i < q2End; i++) {
      months[i].marketing = marketingQ2 / Math.min(3, q2End - q1End);
    }
    // Remaining marketing equally over remaining months
    const remainingMonths = totalMonths - q2End;
    if (remainingMonths > 0) {
      const marketingPerMonth = (totalMarketing * 0.60) / remainingMonths;
      for (let i = q2End; i < totalMonths; i++) {
        months[i].marketing = marketingPerMonth;
      }
    }

    // 6. MISC EXPENSES: Same pattern as marketing (30% Q1, 10% Q2, balance equally)
    const miscQ1 = totalMisc * 0.30;
    const miscQ2 = totalMisc * 0.10;
    for (let i = startIdx; i < q1End; i++) {
      months[i].misc = miscQ1 / Math.min(3, q1End - startIdx);
    }
    for (let i = q1End; i < q2End; i++) {
      months[i].misc = miscQ2 / Math.min(3, q2End - q1End);
    }
    if (remainingMonths > 0) {
      const miscPerMonth = (totalMisc * 0.60) / remainingMonths;
      for (let i = q2End; i < totalMonths; i++) {
        months[i].misc = miscPerMonth;
      }
    }

    // 7. BROKERAGE: 4 equal parts across project duration
    const brokeragePerPart = totalBrokerage / 4;
    const brokerageInterval = Math.max(1, Math.floor(durationMonths / 4));
    for (let part = 0; part < 4; part++) {
      const idx = startIdx + (part * brokerageInterval);
      if (idx < totalMonths) {
        months[idx].brokerage = brokeragePerPart;
      }
    }

    // 8. CONSTRUCTION: Equally distributed across entire project duration
    const constructionPerMonth = durationMonths > 0 ? construction / durationMonths : 0;
    for (let i = startIdx; i < totalMonths; i++) {
      months[i].construction = constructionPerMonth;
    }

    // 9. BANK INTEREST: Equally distributed
    const interestPerMonth = durationMonths > 0 ? bankInterest / durationMonths : 0;
    for (let i = startIdx; i < totalMonths; i++) {
      months[i].bankInterest = interestPerMonth;
    }

    // 10. PMC: Equally distributed
    const pmcPerMonth = durationMonths > 0 ? pmcCost / durationMonths : 0;
    for (let i = startIdx; i < totalMonths; i++) {
      months[i].pmcCost = pmcPerMonth;
    }

    // 11. GST: At end
    if (lastIdx >= 0) {
      months[lastIdx].gst = totalGST;
    }

    // Calculate totals per month
    let runningTotal = 0;
    months.forEach(m => {
      m.total = m.stampDuty + m.registration + m.tdrCost + m.paidFSI + m.ancillaryFSI +
        m.sanctionCosts + m.rentDeposit + m.betterment + m.shifting + m.corpus +
        m.monthlyRent + m.bgCost + m.brokerageSociety + m.parkingCompensation +
        m.marketing + m.misc + m.brokerage + m.construction + m.bankInterest + m.pmcCost + m.gst;
      runningTotal += m.total;
      m.cumulative = runningTotal;
    });

    // ===== BUILD QUARTERLY VIEW =====
    const quarters = [];
    for (let qi = 0; qi < totalMonths; qi += 3) {
      const qMonths = months.slice(qi, Math.min(qi + 3, totalMonths));
      const q = {
        label: qi < preProjectMonths ? "Pre-Project" : `Q${Math.ceil((qi - preProjectMonths + 1) / 3)}`,
        monthRange: `${qMonths[0].monthLabel} to ${qMonths[qMonths.length - 1].monthLabel}`,
        months: qMonths,
        stampDuty: qMonths.reduce((s, m) => s + m.stampDuty, 0),
        registration: qMonths.reduce((s, m) => s + m.registration, 0),
        tdrCost: qMonths.reduce((s, m) => s + m.tdrCost, 0),
        paidFSI: qMonths.reduce((s, m) => s + m.paidFSI, 0),
        ancillaryFSI: qMonths.reduce((s, m) => s + m.ancillaryFSI, 0),
        sanctionCosts: qMonths.reduce((s, m) => s + m.sanctionCosts, 0),
        rentDeposit: qMonths.reduce((s, m) => s + m.rentDeposit, 0),
        betterment: qMonths.reduce((s, m) => s + m.betterment, 0),
        shifting: qMonths.reduce((s, m) => s + m.shifting, 0),
        corpus: qMonths.reduce((s, m) => s + m.corpus, 0),
        monthlyRent: qMonths.reduce((s, m) => s + m.monthlyRent, 0),
        bgCost: qMonths.reduce((s, m) => s + m.bgCost, 0),
        brokerageSociety: qMonths.reduce((s, m) => s + m.brokerageSociety, 0),
        parkingCompensation: qMonths.reduce((s, m) => s + m.parkingCompensation, 0),
        marketing: qMonths.reduce((s, m) => s + m.marketing, 0),
        misc: qMonths.reduce((s, m) => s + m.misc, 0),
        brokerage: qMonths.reduce((s, m) => s + m.brokerage, 0),
        construction: qMonths.reduce((s, m) => s + m.construction, 0),
        bankInterest: qMonths.reduce((s, m) => s + m.bankInterest, 0),
        pmcCost: qMonths.reduce((s, m) => s + m.pmcCost, 0),
        gst: qMonths.reduce((s, m) => s + m.gst, 0),
        total: qMonths.reduce((s, m) => s + m.total, 0),
        cumulative: qMonths[qMonths.length - 1].cumulative,
      };
      quarters.push(q);
    }

    // Grand totals
    const grandTotal = {
      stampDuty, registration: registration, tdrCost, paidFSI: paidFSICost,
      ancillaryFSI: ancillaryCost, sanctionCosts, rentDeposit: depositCost,
      betterment, shifting, corpus, monthlyRent: totalRent,
      bgCost, brokerageSociety, marketing: totalMarketing, misc: totalMisc,
      brokerage: totalBrokerage, construction, bankInterest, pmcCost, gst: totalGST,
      parkingCompensation,
      total: runningTotal,
    };

    return { months, quarters, grandTotal, durationMonths, preProjectMonths, totalMonths };
  }, [formData, plData, fsiCosts, daChargesCosts, societyCosts]);

  if (!cashFlowData) return null;

  const { quarters, grandTotal } = cashFlowData;

  const expenseHeads = [
    { key: "stampDuty", label: "Stamp Duty", group: "DA Charges" },
    { key: "registration", label: "Registration", group: "DA Charges" },
    { key: "tdrCost", label: "TDR Cost", group: "FSI & Sanction" },
    { key: "paidFSI", label: "Paid FSI", group: "FSI & Sanction" },
    { key: "ancillaryFSI", label: "Ancillary FSI", group: "FSI & Sanction" },
    { key: "sanctionCosts", label: "Other Sanction", group: "FSI & Sanction" },
    { key: "rentDeposit", label: "Rent Deposit", group: "Society" },
    { key: "shifting", label: "Shifting", group: "Society" },
    { key: "betterment", label: "Betterment", group: "Society" },
    { key: "monthlyRent", label: "Monthly Rent", group: "Society" },
    { key: "corpus", label: "Corpus", group: "Society" },
    { key: "bgCost", label: "Bank Guarantee", group: "Society" },
    { key: "brokerageSociety", label: "Brokerage (Society)", group: "Society" },
    { key: "parkingCompensation", label: "Parking Compensation", group: "Society" },
    { key: "construction", label: "Construction", group: "Construction" },
    { key: "marketing", label: "Marketing", group: "Other Costs" },
    { key: "misc", label: "Misc Expenses", group: "Other Costs" },
    { key: "brokerage", label: "Brokerage", group: "Other Costs" },
    { key: "bankInterest", label: "Bank Interest", group: "Finance" },
    { key: "pmcCost", label: "PMC/SNA", group: "Finance" },
    { key: "gst", label: "GST", group: "Finance" },
  ];

  // Filter out zero-total heads
  const activeHeads = expenseHeads.filter(h => Math.abs(grandTotal[h.key]) > 0);

  const toggleQuarter = (idx) => {
    setExpandedQuarters(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const groupColors = {
    "DA Charges": "bg-red-50 text-red-700",
    "FSI & Sanction": "bg-blue-50 text-blue-700",
    "Society": "bg-purple-50 text-purple-700",
    "Construction": "bg-amber-50 text-amber-700",
    "Other Costs": "bg-green-50 text-green-700",
    "Finance": "bg-slate-50 text-slate-700",
  };

  return (
    <>
    <Card className="rounded-sm shadow-sm" data-testid="expense-cashflow">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="font-heading tracking-tight">Detailed Expense Cash Flow</CardTitle>
            <CardDescription>
              Month-wise expense distribution ({cashFlowData.preProjectMonths} pre-project + {cashFlowData.durationMonths} project months)
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "quarterly" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("quarterly")}
              className="rounded-sm text-xs"
              data-testid="view-quarterly"
            >
              Quarterly
            </Button>
            <Button
              variant={viewMode === "monthly" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("monthly")}
              className="rounded-sm text-xs"
              data-testid="view-monthly"
            >
              Monthly
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="cashflow-table">
            <thead>
              <tr className="border-b-2 border-foreground/20">
                <th className="text-left p-2 sticky left-0 bg-background z-10 min-w-[140px]">Expense Head</th>
                {viewMode === "quarterly" ? (
                  quarters.map((q, i) => (
                    <th key={i} className="text-right p-2 min-w-[90px] whitespace-nowrap">
                      <div className="font-semibold">{q.label}</div>
                      <div className="text-[10px] text-muted-foreground font-normal">{q.monthRange}</div>
                    </th>
                  ))
                ) : (
                  cashFlowData.months.map((m, i) => (
                    <th key={i} className="text-right p-2 min-w-[75px] whitespace-nowrap">
                      <div className="font-semibold">{m.monthLabel}</div>
                    </th>
                  ))
                )}
                <th className="text-right p-2 min-w-[90px] bg-accent/10 font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {activeHeads.map((head, hIdx) => {
                const prevHead = hIdx > 0 ? activeHeads[hIdx - 1] : null;
                const showGroupHeader = !prevHead || prevHead.group !== head.group;

                return (
                  <>{showGroupHeader && (
                    <tr key={`group-${head.group}`}>
                      <td colSpan={viewMode === "quarterly" ? quarters.length + 2 : cashFlowData.months.length + 2}
                          className={`p-1.5 font-semibold text-[10px] uppercase tracking-wider ${groupColors[head.group] || "bg-secondary"}`}>
                        {head.group}
                      </td>
                    </tr>
                  )}
                  <tr key={head.key} className="border-b border-muted/30 hover:bg-muted/20">
                    <td className="p-2 sticky left-0 bg-background z-10 text-muted-foreground">{head.label}</td>
                    {viewMode === "quarterly" ? (
                      quarters.map((q, i) => (
                        <td key={i} className={`text-right p-2 font-mono ${q[head.key] > 0 ? '' : 'text-muted-foreground/40'}`}>
                          {q[head.key] > 0 ? formatINR(q[head.key]) : "—"}
                        </td>
                      ))
                    ) : (
                      cashFlowData.months.map((m, i) => (
                        <td key={i} className={`text-right p-2 font-mono ${m[head.key] > 0 ? '' : 'text-muted-foreground/40'}`}>
                          {m[head.key] > 0 ? formatINR(m[head.key]) : "—"}
                        </td>
                      ))
                    )}
                    <td className="text-right p-2 font-mono font-semibold bg-accent/10">
                      {grandTotal[head.key] > 0 ? formatINR(grandTotal[head.key]) : "—"}
                    </td>
                  </tr>
                  </>
                );
              })}

              {/* TOTAL ROW */}
              <tr className="border-t-2 border-foreground/30 font-semibold bg-accent/10">
                <td className="p-2 sticky left-0 bg-accent/10 z-10">TOTAL EXPENSES</td>
                {viewMode === "quarterly" ? (
                  quarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono">{formatINR(q.total)}</td>
                  ))
                ) : (
                  cashFlowData.months.map((m, i) => (
                    <td key={i} className="text-right p-2 font-mono">{formatINR(m.total)}</td>
                  ))
                )}
                <td className="text-right p-2 font-mono font-bold">{formatINR(grandTotal.total)}</td>
              </tr>

              {/* CUMULATIVE ROW */}
              <tr className="bg-blue-50/50">
                <td className="p-2 sticky left-0 bg-blue-50/50 z-10 text-blue-700 font-medium">Cumulative</td>
                {viewMode === "quarterly" ? (
                  quarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono text-blue-700">{formatINR(q.cumulative)}</td>
                  ))
                ) : (
                  cashFlowData.months.map((m, i) => (
                    <td key={i} className="text-right p-2 font-mono text-blue-700">{formatINR(m.cumulative)}</td>
                  ))
                )}
                <td className="text-right p-2 font-mono font-bold text-blue-700">{formatINR(grandTotal.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Verification: Cash Flow vs P&L */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-sm text-sm space-y-2">
          <p className="font-semibold text-blue-800">Verification</p>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <span className="text-muted-foreground">Cash Flow Total: </span>
              <span className="font-mono font-semibold">{formatINR(grandTotal.total)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">P&L Total Expenses: </span>
              <span className="font-mono font-semibold">{formatINR(plData?.totalExpenses)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Difference: </span>
              <span className={`font-mono font-semibold ${Math.abs(grandTotal.total - (plData?.totalExpenses || 0)) < 1000 ? 'text-green-600' : 'text-amber-600'}`}>
                {formatINR(Math.abs(grandTotal.total - (plData?.totalExpenses || 0)))}
                {Math.abs(grandTotal.total - (plData?.totalExpenses || 0)) < 1000 ? ' (Matched)' : ''}
              </span>
            </div>
          </div>
          {Math.abs(grandTotal.total - (plData?.totalExpenses || 0)) >= 1000 && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Note: Small differences may occur if society expenses haven't been calculated yet. Visit the Society tab first for exact matching.
            </p>
          )}
        </div>

        {/* Distribution Rules Summary */}
        <div className="mt-6 p-3 bg-muted/30 rounded-sm text-xs text-muted-foreground space-y-1">
          <p className="font-semibold text-foreground text-sm mb-2">Distribution Rules</p>
          <p><span className="font-medium">Pre-Project (M-3):</span> Stamp Duty + Registration</p>
          <p><span className="font-medium">Project Start (M1):</span> All Sanction/FSI costs + Rent Deposit + 50% Shifting + 50% Betterment + BG + Society Brokerage</p>
          <p><span className="font-medium">Monthly:</span> Rent equally distributed</p>
          <p><span className="font-medium">Project End:</span> Corpus + 50% Betterment + 50% Shifting + GST</p>
          <p><span className="font-medium">Marketing/Misc:</span> 30% in Q1, 10% in Q2, balance equally over remaining period</p>
          <p><span className="font-medium">Brokerage:</span> 4 equal parts across project duration</p>
          <p><span className="font-medium">Construction:</span> Equally distributed across entire duration</p>
        </div>
      </CardContent>
    </Card>

    {/* Sale Velocity & Income */}
    <SaleVelocityForm
      formData={formData}
      plData={plData}
      expenseQuarters={quarters}
      expenseMonths={cashFlowData.months}
      preProjectMonths={cashFlowData.preProjectMonths}
    />
  </>
  );
};

export default ExpenseCashFlow;
